import { i18n } from "@bebit/store";
import { DateRange } from "@bebit/types";
import { differenceInDays } from "date-fns";
import { getTimezoneStr } from "./date-formatter.util";

export type Msec = number;
export type Sec = number;
export type Usec = number;

export const HOURLY_INTERVALS_DEFAULT_VALUE = -1;
export const MAX_DISPLAY_SPAN_DAYS = 731;
export const SECONDS_IN_MINUTE: Sec = 60;
export const MINUTES_IN_HOUR = 60;
export const HOURS_IN_DAY = 24;
export const WEEK_IN_DAYS = 7;
export const SECONDS_IN_DAY: Sec = SECONDS_IN_MINUTE * MINUTES_IN_HOUR * HOURS_IN_DAY;
export const TIMEZONE_OFFSET: number = new Date().getTimezoneOffset();
export const HOUR_UNSELECTED = -1;

export function isWithin2Years(date: Date): boolean {
  const date2YearsAgo = getDate2YearsAgo(new Date());
  return differenceInDays(date2YearsAgo, date) <= 0;
}

export function getDate2YearsAgo(date: Date): Date {
  const twoYearsAgo = new Date(date);
  twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

  return twoYearsAgo;
}

export function getTwoYearsAgoStartDate(): Date {
  const date2YearsAgoStart = getDate2YearsAgo(new Date());
  date2YearsAgoStart.setHours(0, 0, 0, 0);
  return date2YearsAgoStart;
}

export function getTwoYearsAgoEndDate(): Date {
  const date2YearsAgoEnd = getDate2YearsAgo(new Date());
  date2YearsAgoEnd.setHours(23, 59, 59, 999);
  return date2YearsAgoEnd;
}

// There is no date format defined in javascript, so use the Java format
export enum DateFormat {
  HHcmm_TZONEDIFF = "%(HH):%(mm)%(TZONE_STR_IFDIFF)",
  HHcmm = "%(HH):%(mm)",
  Md = "%(M)/%(d)",
  yyyysMMsdd = "%(yyyy)/%(MM)/%(dd)",
  yyyysMMsdd_HHcmm = "%(yyyy)/%(MM)/%(dd) %(HH):%(mm)",
  yyyysMsd = "%(yyyy)/%(M)/%(d)",
  yyyyMMdd_HHmmss = "%(yyyy)%(MM)%(dd)_%(HH)%(mm)%(ss)",
  yyyy_MM_dd = "%(yyyy)_%(MM)_%(dd)",

  // The following is the key for translation, so space is cut.
  M月d日_E_HH時mm分_TZONEDIFF = "%(E),%(MM)-%(dd)%(HH):%(mm)%(TZONE_STR_IFDIFF)",
  M月d日_E_HH時mm分 = "%(E),%(MM)-%(dd)%(HH):%(mm)",
  M月d日E = "%(E),%(M)-%(d)",
  yyyy年M月d日E = "%(E),%(M)-%(d)-%(yyyy)",
  yyyy年M月d日 = "%(M)-%(d)-%(yyyy)",
  yyyy年MMM月 = "%(MMM)-%(yyyy)",
  MMM月 = "%(MMM)",
  // %(HH):%(mm) and : are both fine, but the keys overlap, then use -.
  yyyy年M月d日E_HH時mm分_TZONEDIFF = "%(E),%(M)-%(d)-%(yyyy)%(HH)-%(mm)%(TZONE_STR_IFDIFF)",
  yyyy年M月d日E_HHcmm_TZONEDIFF = "%(E),%(M)-%(d)-%(yyyy)%(HH):%(mm)%(TZONE_STR_IFDIFF)",
  yyyy年M月d日E_HH時mm分 = "%(E),%(M)-%(d)-%(yyyy)%(HH)-%(mm)",
}

const monthMap: { [index: number]: string } = {
  1: "Jan",
  2: "Feb",
  3: "Mar",
  4: "Apr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Aug",
  9: "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dec",
};

const dayMap: { [index: number]: string } = {
  0: "Sun",
  1: "Mon",
  2: "Tue",
  3: "Wed",
  4: "Thu",
  5: "Fri",
  6: "Sat",
};

/* eslint-disable-next-line max-lines-per-function */
export function formatDate(format: DateFormat, dtSrcP: Date, timezoneOffsetP: number = TIMEZONE_OFFSET): string {
  if (dtSrcP.getTime() === 0) {
    return "";
  }
  let dtSrc = dtSrcP;
  let timezoneOffset = timezoneOffsetP;

  // Check timezoneOffset type as it may be passed as null or undefined.
  if (typeof timezoneOffset !== "number") {
    timezoneOffset = TIMEZONE_OFFSET;
  }

  // Create time zone display string
  const timeZoneStr = getTimezoneStr(timezoneOffset);

  // Display only when time zones are different
  let timeZoneStrIfDiff = "";

  // If the time zone is different, display in the specified time zone
  if (timezoneOffset !== TIMEZONE_OFFSET) {
    // Add default time zone -> getYear,Month,etc value becomes UTC
    // Subtract the specified time zone -> The value of getYear,Month,etc becomes the value of the specified time zone
    // *Time zone is opposite positive and negative value in seconds (e.g. +09:00 becomes -540)
    const timeZoneDiff = TIMEZONE_OFFSET - timezoneOffset;
    const adjustTimeMsec = dtSrc.getTime() + timeZoneDiff * 60 * 1000;
    dtSrc = new Date(adjustTimeMsec);

    // String to display only when time zones are different
    timeZoneStrIfDiff = timeZoneStr;
  }

  const year: number = dtSrc.getFullYear();
  const month: number = dtSrc.getMonth() + 1;
  const date: number = dtSrc.getDate();
  const hour: number = dtSrc.getHours();
  const minute: number = dtSrc.getMinutes();
  const day: number = dtSrc.getDay();
  const shortDayTx: string = i18n.global.t(`utils.date.${dayMap[day]}`);

  const yy = String(year % 100);
  const yyyy = String(year);
  const M = String(month);
  const MM = M.padStart(2, "0");
  const d = String(date);
  const dd = d.padStart(2, "0");
  const h = String(hour % 12);
  const hh = h.padStart(2, "0");
  const H = String(hour);
  const HH = H.padStart(2, "0");
  const m = String(minute);
  const mm = m.padStart(2, "0");
  const s = String(dtSrc.getSeconds());
  const ss = s.padStart(2, "0");

  const E: string = shortDayTx;

  const MMM: string = i18n.global.t(`utils.date.${monthMap[month]}`);

  const params: { [index: string]: number | string } = {
    YEAR: year,
    MONTH: month,
    DATE: date,
    HOUR: hour,
    MINUTE: minute,
    SHORT_DAY_TX: shortDayTx,
    TZONE_STR_IFDIFF: timeZoneStrIfDiff,
    yy,
    yyyy,
    M,
    MM,
    MMM,
    d,
    dd,
    h,
    hh,
    H,
    HH,
    m,
    mm,
    s,
    ss,
    E,
  };

  let retTemplate: string = format;
  if (isTranslationRequired(format)) {
    retTemplate = i18n.global.t(`utils.date.${format}`);
  }

  const keys: string[] = Object.keys(params);
  keys.forEach((key) => {
    retTemplate = retTemplate.replace("%(" + key + ")", String(params[key]));
  });

  return retTemplate;
}

function isTranslationRequired(format: DateFormat): boolean {
  return [
    DateFormat.M月d日_E_HH時mm分_TZONEDIFF,
    DateFormat.M月d日_E_HH時mm分,
    DateFormat.M月d日E,
    DateFormat.yyyy年M月d日E,
    DateFormat.yyyy年M月d日,
    DateFormat.yyyy年MMM月,
    DateFormat.MMM月,
    DateFormat.yyyy年M月d日E_HH時mm分_TZONEDIFF,
    DateFormat.yyyy年M月d日E_HHcmm_TZONEDIFF,
    DateFormat.yyyy年M月d日E_HH時mm分,
  ].includes(format);
}

export const getSearchPeriodDates = (months: number, days: number, endDateNegativeOffsetDays: number): DateRange => {
  // End of selection dateRange
  const end: Date = new Date();
  end.setDate(end.getDate() - endDateNegativeOffsetDays);
  /**
   * There was a bug where the current time date was passed, so this has been fixed by setting it to 23:59:59.
   * https://bebit.slack.com/archives/CDUED3657/p1574688265252100?thread_ts=1574676074.239300&cid=CDUED3657
   */
  end.setHours(23 /*hours*/, 59 /*min*/, 59 /*sec*/, 999 /*ms*/);

  // Beginning of selection dateRange = End date - {months}months - {days}day
  const start: Date = new Date(end);
  start.setMonth(start.getMonth() - months);
  start.setDate(start.getDate() - days);
  start.setHours(0 /*hours*/, 0 /*min*/, 0 /*sec*/, 0 /*ms*/);

  return { start, end };
};

export function postDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return year + "-" + month + "-" + day;
}

export function getLastMonthStartDate(): Date {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(1);
  date.setMonth(date.getMonth() - 1);
  return date;
}

export function getLastMonthEndDate(): Date {
  const date = new Date();
  date.setHours(23, 59, 59, 999);
  date.setDate(0);
  return date;
}

export function msecToSec(msec: Msec): Sec {
  return Math.floor(msec / 1000);
}

export function msecToUsec(msec: Msec): Usec {
  return Math.floor(msec * 1000);
}

export function usecToMsec(usec: Usec): Msec {
  return Math.floor(usec / 1000);
}

export function secToMsec(sec: Sec): Msec {
  return Math.floor(sec * 1000);
}

export function getElapsedTimeTx(startSec: number, endSec: number): string {
  const params = getTimeDiffTxParams(startSec, endSec, TIMEZONE_OFFSET);
  if (params.type === TIME_DIFF_PARAM_TYPE.TIME_UNIT) {
    return i18n.global.t("utils.date.timeUnitElapsed", {
      time: params.time,
      unit: params.unitTx,
    });
  } else {
    const year = i18n.global.t("utils.date.year", { count: params.year });
    const month = i18n.global.t("utils.date.month", { count: params.month });
    return i18n.global.t("utils.date.yearMonthElapsed", {
      year: year,
      month: month,
    });
  }
}

export enum TIME_DIFF_PARAM_TYPE {
  TIME_UNIT = 1, // %(TIME)%(UNIT)経過、前、などで用いる
  YEAR_MONTH = 2, // %(YEAR)年%(MONTH)ヶ月経過などで用いる
}

export interface TimeDiffTxParams {
  type: TIME_DIFF_PARAM_TYPE;
  time: number;
  unitTx: string;
  year: number;
  month: number;
}

// 履歴用経過テキストを取得
export function getElapsedTxFromNow(targetMsec: number): string {
  const params = getTimeDiffTxParams(msecToSec(targetMsec), msecToSec(Date.now()));
  if (params.type === TIME_DIFF_PARAM_TYPE.TIME_UNIT) {
    return i18n.global.t("utils.date.TIME_UNIT_ago", { time: params.time, unit: params.unitTx });
  } else {
    const year = i18n.global.t("utils.date.year", {
      count: params.year,
    });
    const month = i18n.global.t("utils.date.month", {
      count: params.month,
    });
    return i18n.global.t("utils.date.YEAR_MONTH_ago", { year, month });
  }
}

// eslint-disable-next-line max-lines-per-function
export function getTimeDiffTxParams(
  startSec: number,
  endSec: number,
  timezoneOffset: number = TIMEZONE_OFFSET
): TimeDiffTxParams {
  const minuteSec = 60;
  const hourSec = 60 * minuteSec;
  const daySec = 24 * hourSec;
  const weekSec = 7 * daySec;

  let time = 0;
  let unitTx = "";

  const elapsedSec = endSec - startSec;

  // 1週間未満
  if (elapsedSec < weekSec) {
    if (elapsedSec < minuteSec) {
      time = elapsedSec;
      unitTx = i18n.global.t("utils.date.s");
    } else if (elapsedSec < hourSec) {
      time = Math.floor(elapsedSec / minuteSec);
      unitTx = i18n.global.t("utils.date.min");
    } else if (elapsedSec < daySec) {
      time = Math.floor(elapsedSec / hourSec);
      unitTx = i18n.global.t("utils.date.h");
    } else {
      time = Math.floor(elapsedSec / daySec);
      unitTx = i18n.global.t("utils.date.D");
    }
    return {
      type: TIME_DIFF_PARAM_TYPE.TIME_UNIT,
      time: time,
      unitTx: unitTx,
      year: -1,
      month: -1,
    };
  }
  // 単位が週以上の場合、年月日を取得し、週、ヶ月、年を取得する
  // timezoneを調整する
  const timezoneDiffSec = (TIMEZONE_OFFSET - timezoneOffset) * 60;

  const startDt = new Date((startSec + timezoneDiffSec) * 1000);
  const endDt = new Date((endSec + timezoneDiffSec) * 1000);
  const nextOfEndDt = new Date(endSec * 1000 + 24 * 60 * 60 * 1000);

  const startYear = startDt.getFullYear();
  const startMonth = startDt.getMonth() + 1;
  const startDate = startDt.getDate();

  const endYear = endDt.getFullYear();
  const endMonth = endDt.getMonth() + 1;
  const endDate = endDt.getDate();
  const nextOfEndDate = nextOfEndDt.getDate();

  // 累計経過月数を算出する
  let elapsedTotalMonth = (endYear - startYear) * 12 + (endMonth - startMonth);

  // 月中の終了の日＜月中の開始の日で、かつ終了日＜＞月末の場合、１か月経っていないものとみなす
  // （例：04/25 - 05/02など)（例外：01/29(or 30 or 31) - 02/28は1ヶ月経っているものとみなす)
  if (endDate < startDate && nextOfEndDate !== 1) {
    elapsedTotalMonth = elapsedTotalMonth - 1;
  }

  if (12 <= elapsedTotalMonth) {
    // 1年以上経っている場合
    const elapsedYear = Math.floor(elapsedTotalMonth / 12);
    const elapsedMonth = elapsedTotalMonth % 12;
    if (elapsedMonth === 0) {
      const unitTx = i18n.global.t("utils.date.YR");
      return {
        type: TIME_DIFF_PARAM_TYPE.TIME_UNIT,
        time: elapsedYear,
        unitTx: unitTx,
        year: -1,
        month: -1,
      };
    } else {
      return {
        type: TIME_DIFF_PARAM_TYPE.YEAR_MONTH,
        time: -1,
        unitTx: "",
        year: elapsedYear,
        month: elapsedMonth,
      };
    }
  } else {
    // 1年未満の場合
    if (elapsedTotalMonth === 0) {
      // 1ヶ月未満の場合（同月、もしくは翌月の日付が末日以外で開始日未満）
      const elapsedWeek = Math.floor(elapsedSec / weekSec);
      const unitTx = i18n.global.t("utils.date.WK");
      return {
        type: TIME_DIFF_PARAM_TYPE.TIME_UNIT,
        time: elapsedWeek,
        unitTx: unitTx,
        year: -1,
        month: -1,
      };
    } else {
      // 1ヶ月以上の場合
      const unitTx = i18n.global.t("utils.date.M");

      return {
        type: TIME_DIFF_PARAM_TYPE.TIME_UNIT,
        time: elapsedTotalMonth,
        unitTx: unitTx,
        year: -1,
        month: -1,
      };
    }
  }
}

/**
 * 利用期間を単位付きで返す
 */
// eslint-disable-next-line max-lines-per-function
export function usagePeriod(startDate: Date, endDate: Date, timezoneOffset: number = TIMEZONE_OFFSET): string {
  // timezoneを調整する
  const timezoneDiffSec = (TIMEZONE_OFFSET - timezoneOffset) * 60;

  const startSec = startDate.getTime() / 1000 + timezoneDiffSec;
  const endSec = endDate.getTime() / 1000 + timezoneDiffSec;

  const startDt = new Date(startSec * 1000);
  const endDt = new Date(endSec * 1000);
  const nextOfEndDt = new Date(endSec * 1000 + 24 * 60 * 60 * 1000);

  const startYear = startDt.getFullYear();
  const startMonth = startDt.getMonth() + 1;
  const startDay = startDt.getDate();

  const endYear = endDt.getFullYear();
  const endMonth = endDt.getMonth() + 1;
  const endDay = endDt.getDate();
  const nextOfEndDay = nextOfEndDt.getDate();

  // 累計経過月数を算出する
  let elapsedTotalMonth = (endYear - startYear) * 12 + (endMonth - startMonth);

  // 月中の終了の日＜月中の開始の日で、かつ終了日＜＞月末の場合、１か月経っていないものとみなす
  // （例：04/25 - 05/02など)（例外：01/29(or 30 or 31) - 02/28は1ヶ月経っているものとみなす)
  if (endDay < startDay && nextOfEndDay !== 1) {
    elapsedTotalMonth = elapsedTotalMonth - 1;
  }

  if (elapsedTotalMonth >= 12) {
    // 1年以上経っている場合
    const elapsedYear = Math.floor(elapsedTotalMonth / 12);
    const elapsedMonth = elapsedTotalMonth % 12;
    if (elapsedMonth === 0) {
      return `${elapsedYear}${i18n.global.t("utils.date.YR")}`;
    } else {
      const year = `${elapsedYear}${i18n.global.t("utils.date.YR")}`;
      const month = `${elapsedMonth}${i18n.global.t("utils.date.M")}`;
      return `${year}${month}`;
    }
  } else {
    // 1年未満の場合
    if (elapsedTotalMonth === 0) {
      // 1ヶ月未満の場合
      const elapsedDay = Math.floor((endSec - startSec) / 60 / 60 / 24);
      // 小数点1位までの数字をセットする
      const elapsedMonth = Math.floor((elapsedDay / 31) * 10) / 10;
      return `${elapsedMonth}${i18n.global.t("utils.date.M")}`;
    } else {
      return `${elapsedTotalMonth}${i18n.global.t("utils.date.M")}`;
    }
  }
}

export function translateMonthToString(month: number): string {
  const monthName = monthMap[month];
  return i18n.global.t("utils.date." + monthName);
}

export function resetTime(date: Date): Date {
  date.setHours(0, 0, 0, 0);
  return date;
}

export function translateMonthAndYearToString(month: number, year: number): string {
  const translatedMonth = translateMonthToString(month);
  return i18n.global.t("utils.date.monthYear", {
    month: translatedMonth,
    year,
  });
}

export function translateDateToString(date: Date): string {
  const month: number = date.getMonth() + 1; // add 1 because january == 0

  if (month === 1) {
    const year: number = date.getFullYear();
    return translateMonthAndYearToString(month, year);
  }

  return translateMonthToString(month);
}

export function toLocalHour(utcHour: number): number | null {
  if (utcHour === HOUR_UNSELECTED) {
    return null;
  }
  return (utcHour - new Date().getTimezoneOffset() / MINUTES_IN_HOUR + HOURS_IN_DAY) % HOURS_IN_DAY;
}

interface Interval {
  start: Sec;
  end: Sec;
}

export function getHourlyInterval(date: Date, hour: number): Interval {
  const start: Date = new Date(date.valueOf());
  start.setHours(hour);
  start.setMinutes(0);
  start.setSeconds(0);
  start.setMilliseconds(0);
  const startSec: Sec = msecToSec(start.valueOf());

  return {
    start: startSec,
    end: startSec + SECONDS_IN_MINUTE * MINUTES_IN_HOUR - 1,
  };
}

/**
 * returns deepCopied set midnight time DateRange
 * if input 2020/01/10 10:00 - 2020/01/11 10:00
 * returns 2020/01/10 0:00:00 - 2020/01/11 23:59:59.999
 * @param dateRange DateRange
 * @returns DateRange that time has changed
 */
export function getDeepCopiedMidnightDateRange(dateRange: DateRange): DateRange {
  const copiedDateRange = {
    start: new Date(dateRange.start),
    end: new Date(dateRange.end),
  };
  copiedDateRange.start.setHours(0, 0, 0, 0);
  copiedDateRange.end.setHours(23, 59, 59, 999);
  return copiedDateRange;
}

export function changeDateRange(dates: Date[]): DateRange {
  const start = dates[1] > dates[0] ? dates[0] : dates[1];
  const end = dates[1] > dates[0] ? dates[1] : dates[0];

  return getDeepCopiedMidnightDateRange({ start, end });
}

export function isSameDateTimeZone(date1: Date, date2: Date, timezone: number): boolean {
  const tzOffsetMsec = timezone * 60 * 1000;
  const d1 = new Date(date1.getTime() + tzOffsetMsec);
  const d2 = new Date(date2.getTime() + tzOffsetMsec);
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}
